/**
 * One row per person for the whole project: hours, cost, and what their time is
 * worth at their contracted rate.
 *
 * Value comes from DemandPerson.Rate x hours -- a measured figure, not an
 * allocation. It is deliberately NOT called revenue: revenue reaches a project
 * as invoice lines keyed by InvoiceID, and on a fixed-fee demand it need not
 * resemble the rate card. Measured across the warehouse, hours-at-rate and
 * recognised revenue are a median 30% apart, and only 21% of demand-months
 * agree within 1%. So this table answers "is this person's time earning its
 * rate", which is a real question about the team, and the project's own margin
 * above answers what the client actually paid.
 *
 * 46% of worked hours carry no rate at all. Those rows show no value rather
 * than a zero, and the footer says how much of the project is unpriced --
 * a total that silently omits half the hours is worse than no total.
 */
import { MarginCell } from "@/components/profitability/MarginCell";
import { DataTable, ROW_CLASS, TOTAL_ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { COLUMN_HELP } from "@/lib/column-help";
import type { ProjectPersonRow } from "@/lib/profitability-api";

const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export interface PersonTotal {
  person: string;
  hours: number;
  cost: number;
  hourlyCost: number | null;
  /** Hours x contracted rate. Null when none of the hours carry a rate. */
  value: number | null;
  ratedHours: number;
  /** Value less cost. Null when there is no value to compare cost against. */
  profit: number | null;
  marginPct: number | null;
  /** The person's realised rate: value / rated hours. */
  effectiveRate: number | null;
}

export function rollUpPeople(rows: ProjectPersonRow[]): PersonTotal[] {
  const totals = new Map<
    string,
    { hours: number; cost: number; value: number; ratedHours: number; anyRate: boolean }
  >();
  for (const row of rows) {
    const bucket = totals.get(row.person) ?? {
      hours: 0,
      cost: 0,
      value: 0,
      ratedHours: 0,
      anyRate: false,
    };
    bucket.hours += row.hours;
    bucket.cost += row.cost;
    if (row.billable_value !== null) {
      bucket.value += row.billable_value;
      bucket.ratedHours += row.rated_hours;
      bucket.anyRate = true;
    }
    totals.set(row.person, bucket);
  }

  return [...totals.entries()]
    .map(([person, b]) => {
      // Null, not zero, when nothing was priced. A zero profit is a claim;
      // "this person's time has no rate on this project" is the truth.
      const value = b.anyRate ? b.value : null;
      return {
        person,
        hours: b.hours,
        cost: b.cost,
        hourlyCost: b.hours > 0 ? b.cost / b.hours : null,
        value,
        ratedHours: b.ratedHours,
        // Cost covers ALL the person's hours while value covers only the rated
        // ones, so a partially-rated person's profit would subtract cost the
        // value never included. Compare like with like: scale cost to the rated
        // share of their hours.
        profit: value === null ? null : value - b.cost * (b.hours > 0 ? b.ratedHours / b.hours : 0),
        marginPct:
          value === null || value === 0
            ? null
            : ((value - b.cost * (b.hours > 0 ? b.ratedHours / b.hours : 0)) / value) * 100,
        effectiveRate: b.ratedHours > 0 ? b.value / b.ratedHours : null,
      };
    })
    .sort((a, b) => b.hours - a.hours);
}

export function PeopleRollup({
  rows,
  filtered = false,
}: {
  rows: ProjectPersonRow[];
  /**
   * True when `rows` is a filtered subset of the project.
   *
   * It changes two things, and both are correctness rather than polish. The
   * Total row is labelled "Total (filtered)", because every figure in it is
   * computed from `rows` and so already covers the subset -- an unqualified
   * "Total" over filtered rows is the single easiest way to read a wrong number
   * off this page. And the empty state stops claiming nobody is assigned to the
   * project when the truth is that nobody matched the filter.
   */
  filtered?: boolean;
}) {
  const totals = rollUpPeople(rows);
  const sumHours = totals.reduce((s, r) => s + r.hours, 0);
  const sumCost = totals.reduce((s, r) => s + r.cost, 0);
  const sumValue = totals.reduce((s, r) => s + (r.value ?? 0), 0);
  const sumRatedHours = totals.reduce((s, r) => s + r.ratedHours, 0);
  // Cost scaled to the priced share, so the total profit compares like with
  // like rather than charging unpriced hours against priced value.
  const ratedCost = totals.reduce(
    (s, r) => s + r.cost * (r.hours > 0 ? r.ratedHours / r.hours : 0),
    0,
  );
  const unpricedHours = sumHours - sumRatedHours;

  return (
    <>
      <DataTable
        flush
        headers={[
          { label: "Person", help: COLUMN_HELP.person },
          // Hours carries the "(n priced)" note, and "priced" is the word the
          // whole right-hand half of this table turns on -- Value, Profit and
          // Margin all count priced hours only. Defined here, where the note
          // itself is, rather than only in the footnote below the table.
          {
            label: "Hours",
            className: "text-right",
            help: `${COLUMN_HELP.personHours} ${COLUMN_HELP.pricedHours}`,
          },
          { label: "Cost", className: "text-right", help: COLUMN_HELP.personLoadedCost },
          { label: "Cost / hr", className: "text-right", help: COLUMN_HELP.personCostRate },
          { label: "Rate", className: "text-right", help: COLUMN_HELP.personRate },
          { label: "Value at rate", className: "text-right", help: COLUMN_HELP.valueAtRate },
          { label: "Profit", className: "text-right", help: COLUMN_HELP.personProfit },
          { label: "Margin", className: "text-right", help: COLUMN_HELP.personMargin },
        ]}
        empty={
          filtered ? (
            <EmptyState
              title="No one matches this filter"
              description="No hours on this project match the filters that are on. Clear or widen them to see the rest."
            />
          ) : (
            <EmptyState title="No one assigned" description="No one is assigned to this project." />
          )
        }
      >
        {/* `null` rather than an empty array when there is nothing to show.
            DataTable decides whether to render its empty state with
            `children.filter(Boolean).length`, and an empty ARRAY is truthy --
            so this table's empty state had never once rendered. It went
            unnoticed while the only way to empty the table was a project with
            nobody on it; a filter makes it reachable on every project. */}
        {totals.length === 0
          ? null
          : totals.map((row) => (
              <tr key={row.person} className={ROW_CLASS}>
                <td className="px-5 py-3 font-medium">{row.person}</td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {row.hours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  {row.ratedHours > 0 && row.ratedHours < row.hours - 0.01 ? (
                    <span
                      className="ml-1 text-xs text-muted-foreground"
                      // "priced" on its own is jargon. The hover says what it
                      // means in full, because this note is the reason Value and
                      // Profit on the same row cover less time than Hours does.
                      title={`${row.ratedHours.toFixed(1)} of these ${row.hours.toFixed(1)} hours are billable AND carry a contracted rate. Value at rate, Profit and Margin count only those.`}
                    >
                      ({row.ratedHours.toFixed(0)} priced)
                    </span>
                  ) : null}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">{money(row.cost)}</td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {row.hourlyCost === null ? "--" : money(row.hourlyCost)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {row.effectiveRate === null ? "--" : money(row.effectiveRate)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {row.value === null ? "--" : money(row.value)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {row.profit === null ? "--" : money(row.profit)}
                </td>
                <td className="px-5 py-3 text-right">
                  <MarginCell value={row.marginPct} />
                </td>
              </tr>
            ))}
        {totals.length > 0 ? (
          <tr className={TOTAL_ROW_CLASS}>
            <td className="px-5 py-3">
              {filtered ? (
                <span title="Every figure in this row is summed from the filtered rows above, not from the whole project.">
                  Total (filtered)
                </span>
              ) : (
                "Total"
              )}
            </td>
            <td className="px-5 py-3 text-right tabular-nums">
              {sumHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}
            </td>
            <td className="px-5 py-3 text-right tabular-nums">{money(sumCost)}</td>
            <td className="px-5 py-3" />
            <td className="px-5 py-3 text-right tabular-nums">
              {sumRatedHours > 0 ? money(sumValue / sumRatedHours) : "--"}
            </td>
            <td className="px-5 py-3 text-right tabular-nums">
              {sumRatedHours > 0 ? money(sumValue) : "--"}
            </td>
            <td className="px-5 py-3 text-right tabular-nums">
              {sumRatedHours > 0 ? money(sumValue - ratedCost) : "--"}
            </td>
            <td className="px-5 py-3 text-right">
              <MarginCell value={sumValue > 0 ? ((sumValue - ratedCost) / sumValue) * 100 : null} />
            </td>
          </tr>
        ) : null}
      </DataTable>
      {unpricedHours > 0.01 && totals.length > 0 ? (
        <p className="px-5 py-3 text-xs text-muted-foreground">
          {unpricedHours.toLocaleString(undefined, { maximumFractionDigits: 0 })} of{" "}
          {sumHours.toLocaleString(undefined, { maximumFractionDigits: 0 })} hours have no rate set
          and are not priced here. Value and profit cover the priced hours only.
        </p>
      ) : null}
    </>
  );
}
